import { useEffect, useState } from "react";
import { Building2, Sparkles, Save, Loader2, Check, Globe2, Briefcase, Compass, User as UserIcon, Languages, AlertTriangle, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { NoamCapabilitiesCard } from "@/components/NoamCapabilitiesCard";
import { notify, fmtError } from "@/lib/notify";
import { useTranslation } from "react-i18next";
import EngineSettingsCard from "@/components/EngineSettingsCard";
import SectorCombobox from "@/components/SectorCombobox";
import SystemsCard from "@/components/SystemsCard";
import LlmKeysCard from "@/components/LlmKeysCard";

const SECTORS = ["Banque", "Assurance", "Santé", "Industrie", "PME", "Autre"];
const SIZES = ["TPE", "PME", "ETI", "Grand groupe", "Institution"];
const PHASES = ["Stabilisation", "Déploiement", "Restructuration"];

export default function Settings() {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState({
    name: "",
    sector: "",
    size: "",
    phase: "Stabilisation",
    country: "Suisse",
    description: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/workspace/organisation")
      .then((r) => {
        setForm({
          name: r.data.name || "",
          sector: r.data.sector || "",
          size: r.data.size || "",
          phase: r.data.phase || "Stabilisation",
          country: r.data.country || "Suisse",
          description: r.data.description || "",
        });
      })
      .catch(() => setError(t("settings.load_error")))
      .finally(() => setLoading(false));
    // Mount-only fetch — `t` and `api` are stable references. Including them
    // in the dep array would re-fetch on every language change which is
    // unnecessary (translation keys are resolved at render time).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const onSave = async (e) => {
    e?.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api.patch("/workspace/organisation", form);
      setSavedAt(Date.now());
      // Notify topbar to re-fetch org name
      window.dispatchEvent(new CustomEvent("noam:org-updated"));
      setTimeout(() => setSavedAt(null), 2200);
    } catch (err) {
      setError(err?.response?.data?.detail || t("settings.save_error"));
    } finally {
      setSaving(false);
    }
  };

  const onAiSuggest = async () => {
    if (!form.name && !form.sector) {
      setError(t("settings.ai_need_input"));
      return;
    }
    setAiLoading(true);
    setError("");
    try {
      const r = await api.post("/workspace/ai/suggest-description", {
        name: form.name,
        sector: form.sector,
        size: form.size,
        country: form.country,
        language: i18n.language,
      });
      setForm((p) => ({ ...p, description: r.data.description }));
    } catch (err) {
      setError(err?.response?.data?.detail || t("settings.ai_error"));
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-zinc-400 text-sm flex items-center gap-2" data-testid="settings-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> {t("common.loading")}
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6" data-testid="settings-page">
      <header>
        <div className="flex items-center gap-2 text-[10px] font-semibold tracking-[0.18em] uppercase text-[#378ADD]">
          <Building2 className="w-3 h-3" /> {t("settings.section")}
        </div>
        <h1 className="noam-display text-3xl mt-2">{t("settings.title")}</h1>
        <p className="text-sm text-zinc-400 mt-1">{t("settings.subtitle")}</p>
      </header>

      <UserPreferencesCard />
      <LlmKeysCard />
      <NoamCapabilitiesCard />
      <SystemsCard />
      <LlmQuotaCard />
      <ModulesCard />
      <DangerZoneCard />

      <form onSubmit={onSave} className="noam-card p-6 space-y-5">
        {/* Name */}
        <div>
          <label className="text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-1.5 mb-1.5">
            <Building2 className="w-3 h-3" /> {t("settings.name")}
          </label>
          <input
            type="text"
            value={form.name}
            onChange={update("name")}
            required
            placeholder={t("settings.name_placeholder")}
            className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none"
            data-testid="settings-name-input"
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {/* Sector — V23.12 combobox catégorisée avec ~95 secteurs + saisie libre */}
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-1.5 mb-1.5">
              <Briefcase className="w-3 h-3" /> {t("settings.sector")}
            </label>
            <SectorCombobox
              value={form.sector}
              onChange={(v) => setForm((f) => ({ ...f, sector: v }))}
              placeholder={t("settings.sector_placeholder")}
              testId="settings-sector-select"
            />
          </div>

          {/* Size */}
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-400 mb-1.5 block">
              {t("settings.size")}
            </label>
            <select
              value={form.size}
              onChange={update("size")}
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none"
              data-testid="settings-size-select"
            >
              <option value="">—</option>
              {SIZES.map((s) => (
                <option key={s} value={s}>{t(`settings.values.size.${s}`, s)}</option>
              ))}
            </select>
          </div>

          {/* Country */}
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-1.5 mb-1.5">
              <Globe2 className="w-3 h-3" /> {t("settings.country")}
            </label>
            <input
              type="text"
              value={form.country}
              onChange={update("country")}
              placeholder="Suisse"
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none"
              data-testid="settings-country-input"
            />
          </div>

          {/* Phase */}
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-1.5 mb-1.5">
              <Compass className="w-3 h-3" /> {t("settings.phase")}
            </label>
            <select
              value={form.phase}
              onChange={update("phase")}
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none"
              data-testid="settings-phase-select"
            >
              {PHASES.map((p) => (
                <option key={p} value={p}>{t(`settings.values.phase.${p}`, p)}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Description with AI */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs uppercase tracking-wider text-zinc-400">
              {t("settings.description")}
            </label>
            <button
              type="button"
              onClick={onAiSuggest}
              disabled={aiLoading}
              className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md bg-[#378ADD]/15 border border-[#378ADD]/40 text-[#B5D4F4] hover:bg-[#378ADD]/25 disabled:opacity-50 transition-colors"
              data-testid="settings-ai-suggest-btn"
            >
              {aiLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              {t("settings.ai_suggest")}
            </button>
          </div>
          <textarea
            value={form.description}
            onChange={update("description")}
            rows={4}
            placeholder={t("settings.description_placeholder")}
            className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none resize-none"
            data-testid="settings-description-textarea"
          />
          <p className="text-[11px] text-zinc-500 mt-1">{t("settings.description_help")}</p>
        </div>

        {error && (
          <div className="text-sm text-[#E24B4A] bg-[#E24B4A]/10 border border-[#E24B4A]/30 rounded-md px-3 py-2" data-testid="settings-error">
            {error}
          </div>
        )}

        <div className="flex items-center justify-between pt-2 border-t border-white/5">
          <div className="text-xs text-zinc-500">
            {savedAt && (
              <span className="inline-flex items-center gap-1 text-[#1D9E75]" data-testid="settings-saved-msg">
                <Check className="w-3 h-3" /> {t("settings.saved")}
              </span>
            )}
          </div>
          <button
            type="submit"
            disabled={saving}
            className="btn-primary text-sm disabled:opacity-50"
            data-testid="settings-save-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {t("common.save")}
          </button>
        </div>
      </form>
      <EngineSettingsCard />
    </div>
  );
}

function UserPreferencesCard() {
  const { user, updatePreferences } = useAuth();
  const [name, setName] = useState("");
  const [locale, setLocale] = useState("fr");
  const [emailTheme, setEmailTheme] = useState("noam");
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState("");
  // Profil NOAM 2026-09 — prénom + rôle + registre
  const [firstName, setFirstName] = useState("");
  const [roleTitle, setRoleTitle] = useState("");
  const [registre, setRegistre] = useState("");

  useEffect(() => {
    if (user && typeof user === "object") {
      setName(user.name || "");
      setLocale(user.locale || "fr");
      setEmailTheme(user.email_theme || "noam");
    }
  }, [user]);

  useEffect(() => {
    api.get("/workspace/intelligence/v2/profile")
      .then((r) => {
        setFirstName(r.data?.first_name || "");
        setRoleTitle(r.data?.role_title || "");
        setRegistre(r.data?.registre_forced || "");
      })
      .catch(() => {});
  }, []);

  if (!user || typeof user !== "object") return null;

  const save = async (e) => {
    e?.preventDefault();
    setBusy(true); setErr("");
    try {
      await updatePreferences({ name: name.trim(), locale, email_theme: emailTheme });
      await api.put("/workspace/intelligence/v2/profile", {
        first_name: firstName, role_title: roleTitle, registre_forced: registre,
      });
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2200);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Erreur.");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="noam-card p-6 space-y-4" data-testid="user-prefs-card">
      <div>
        <div className="flex items-center gap-2 text-[10px] font-semibold tracking-[0.18em] uppercase text-[#7F77DD]">
          <UserIcon className="w-3 h-3" /> Profil utilisateur
        </div>
        <div className="text-sm text-zinc-400 mt-1">Préférences personnelles — appliquées dès la prochaine connexion.</div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-wider text-zinc-400 mb-1.5 block">Nom complet</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={user.email}
            className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#7F77DD] focus:outline-none" data-testid="user-prefs-name" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-1.5 mb-1.5">
            <Languages className="w-3 h-3" /> Langue préférée
          </label>
          <select value={locale} onChange={(e) => setLocale(e.target.value)}
            className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#7F77DD] focus:outline-none" data-testid="user-prefs-locale">
            <option value="fr">Français</option>
            <option value="en">English</option>
            <option value="de">Deutsch</option>
          </select>
          <p className="text-[11px] text-zinc-500 mt-1">Active immédiatement après enregistrement et prioritaire sur la langue du navigateur.</p>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-zinc-400 mb-1.5 block">Thème des emails</label>
          <select value={emailTheme} onChange={(e) => setEmailTheme(e.target.value)}
            className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#B0872E] focus:outline-none" data-testid="user-prefs-email-theme">
            <option value="noam">NOAM (sombre)</option>
            <option value="institutionnel">Institutionnel (clair)</option>
          </select>
          <p className="text-[11px] text-zinc-500 mt-1">Appliqué aux emails de veille et digests (brief du matin, Noam Thinking). Les emails techniques et de sécurité restent au format standard.</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-4 pt-1">
        <div>
          <label className="text-xs uppercase tracking-wider text-zinc-400 mb-1.5 block">Prénom (connu de NOAM)</label>
          <input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="ex. Olivier"
            className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#7F77DD] focus:outline-none" data-testid="user-prefs-firstname" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-zinc-400 mb-1.5 block">Rôle / fonction</label>
          <input value={roleTitle} onChange={(e) => setRoleTitle(e.target.value)} placeholder="ex. CEO, CFO, Risk Manager…"
            className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#7F77DD] focus:outline-none" data-testid="user-prefs-role" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-zinc-400 mb-1.5 block">Registre avec NOAM</label>
          <select value={registre} onChange={(e) => setRegistre(e.target.value)}
            className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#7F77DD] focus:outline-none" data-testid="user-prefs-registre">
            <option value="">Automatique (s'adapte à vous)</option>
            <option value="vous">Vouvoiement</option>
            <option value="tu">Tutoiement</option>
          </select>
          <p className="text-[11px] text-zinc-500 mt-1">NOAM connaîtra votre prénom et votre rôle dès le premier message.</p>
        </div>
      </div>
      {err && <div className="text-xs text-[#E24B4A] bg-[#E24B4A]/10 border border-[#E24B4A]/30 rounded px-3 py-2">{err}</div>}
      <div className="flex items-center justify-between pt-2 border-t border-white/5">
        <div className="text-xs text-zinc-500">
          {savedAt && <span className="inline-flex items-center gap-1 text-[#1D9E75]" data-testid="user-prefs-saved"><Check className="w-3 h-3" /> Préférences enregistrées</span>}
        </div>
        <button type="submit" disabled={busy} className="btn-primary text-sm disabled:opacity-50" data-testid="user-prefs-save">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Enregistrer
        </button>
      </div>
    </form>
  );
}

// ============================================================================
// P2 Vague 10 — LLM Quota Card (user-facing meter, plan + breakdown)
// ============================================================================
function LlmQuotaCard() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/workspace/llm-quota")
      .then((r) => setState(r.data))
      .catch(() => setState(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="noam-card p-6" data-testid="llm-quota-card">
        <Loader2 className="w-4 h-4 animate-spin text-zinc-500" />
      </div>
    );
  }
  if (!state) return null;

  const pct = state.percent || 0;
  const isOver = pct >= 100;
  const isHigh = pct >= 80;
  const barColor = isOver ? "#EF4444" : isHigh ? "#EF9F27" : "#1D9E75";
  const planLabel = {
    base: "Base — 1 000 / mois",
    pro: "Pro — 5 000 / mois",
    enterprise: "Enterprise — 25 000 / mois",
    internal: "Interne — 100 000 / mois",
  }[state.plan] || state.plan;

  const breakdown = Object.entries(state.by_endpoint || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8);

  return (
    <div className="noam-card p-6 space-y-4" data-testid="llm-quota-card">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 text-[10px] font-semibold tracking-[0.18em] uppercase text-[#EF9F27]">
            <Sparkles className="w-3 h-3" /> Quota NOAM · {state.year_month}
          </div>
          <h2 className="noam-display text-xl mt-1">Consommation IA mensuelle</h2>
          <p className="text-xs text-zinc-400 mt-1">{planLabel}</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold" style={{ color: barColor }}>
            {state.used.toLocaleString("fr-CH")} <span className="text-xs text-zinc-500">/ {state.limit.toLocaleString("fr-CH")}</span>
          </div>
          <div className="text-[10px] text-zinc-500 mt-0.5">{pct.toFixed(1)} % utilisé</div>
        </div>
      </div>

      <div className="h-2 rounded-full bg-white/5 overflow-hidden">
        <div
          className="h-full transition-all duration-500"
          style={{ width: `${Math.min(100, pct)}%`, background: barColor }}
        />
      </div>

      {breakdown.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Par endpoint (top 8)</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {breakdown.map(([k, v]) => (
              <div key={k} className="text-xs bg-white/[0.02] rounded p-2 border border-white/5">
                <div className="text-zinc-300 truncate" title={k}>{k}</div>
                <div className="text-zinc-500 text-[10px] mt-0.5">{v} appel{v > 1 ? "s" : ""}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isOver && (
        <div className="text-xs text-red-400 bg-red-500/5 border border-red-500/20 rounded p-3">
          Quota mensuel atteint. Toute nouvelle requête IA sera bloquée jusqu'au {nextMonthLabel(state.year_month)}.
          Contactez l'Ultra Admin pour upgrader votre plan.
        </div>
      )}
      {!isOver && isHigh && (
        <div className="text-xs text-[#EF9F27] bg-[#EF9F27]/5 border border-[#EF9F27]/20 rounded p-3">
          Plus de 80 % du quota mensuel consommé. Économisez les requêtes lourdes ou demandez un upgrade.
        </div>
      )}
    </div>
  );
}

function nextMonthLabel(ym) {
  if (!ym) return "1er du mois prochain";
  const [y, m] = ym.split("-").map(Number);
  const nm = m === 12 ? 1 : m + 1;
  const ny = m === 12 ? y + 1 : y;
  return `1er ${String(nm).padStart(2, "0")}/${ny}`;
}


// V23.16.7 — Carte de gestion des modules visibles par l'org.
// Affiche tous les modules disponibles, leur état (in_plan / extra / disabled / locked),
// permet aux roles autorisés (super_admin / admin+contributeur si allow_role_module_toggle)
// de toggler chaque module. Le super_admin a en plus un toggle global "autoriser les
// admins/contributeurs à modifier les modules".
function ModulesCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [filter, setFilter] = useState("");
  const [permBusy, setPermBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/workspace/modules/state");
      setData(r.data);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Chargement impossible.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (m) => {
    if (m.ultra_locked) return;
    if (!data?.viewer_can_toggle) return;
    setBusy(m.id);
    try {
      await api.put(`/workspace/modules/${m.id}/toggle`, { enabled: !m.effective_visible });
      await load();
      window.dispatchEvent(new CustomEvent("noam:modules-updated"));
    } catch (e) {
      setError(e?.response?.data?.detail || "Action impossible.");
    } finally {
      setBusy("");
    }
  };

  const togglePermission = async () => {
    if (!data) return;
    setPermBusy(true);
    try {
      const next = !data.allow_role_module_toggle;
      await api.patch("/workspace/org/module-permissions", { allow_role_module_toggle: next });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Action impossible.");
    } finally {
      setPermBusy(false);
    }
  };

  if (loading) return (
    <div className="noam-card p-5 text-zinc-500 text-sm" data-testid="modules-card-loading">
      <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-2" /> Chargement des modules…
    </div>
  );
  if (!data) return null;

  const role = data.viewer_role;
  const isSuperOrUltra = role === "super_admin" || role === "ultra_admin";
  const canToggle = data.viewer_can_toggle;
  const q = filter.trim().toLowerCase();
  const modules = (data.modules || []).filter((m) =>
    !q || m.label.toLowerCase().includes(q) || m.id.includes(q)
  );

  return (
    <div className="noam-card p-5 space-y-3" data-testid="modules-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#7F77DD] font-semibold">
            <Sparkles className="w-3 h-3" /> Modules visibles
          </div>
          <h3 className="text-sm font-semibold text-zinc-100 mt-1">Personnaliser les modules de votre environnement</h3>
          <p className="text-[11px] text-zinc-500 mt-1 leading-snug max-w-2xl">
            Activez ou désactivez les modules selon votre usage réel. Réduit le bruit en masquant
            les modules non concernés. Vous pouvez aussi activer des modules hors plan (selon les
            permissions définies).
          </p>
        </div>
        <div className="text-right text-[11px] text-zinc-500">
          <div data-testid="modules-card-totals">
            <span className="text-[#1D9E75] font-semibold">{data.totals.effective_visible}</span> / {data.totals.total} visibles
          </div>
          <div className="text-[10px] text-zinc-600 mt-0.5">
            Plan : <span className="text-zinc-400">{data.plan_id}</span> · Rôle : <span className="text-zinc-400">{role || "—"}</span>
          </div>
        </div>
      </div>

      {error && <div className="text-xs text-[#FCA5A5]">{error}</div>}

      {isSuperOrUltra && (
        <div className={`p-3 rounded border ${data.allow_role_module_toggle ? "bg-[#1D9E75]/8 border-[#1D9E75]/30" : "bg-white/[0.02] border-white/10"}`} data-testid="modules-permission-toggle">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={data.allow_role_module_toggle}
              onChange={togglePermission}
              disabled={permBusy}
              className="mt-0.5 accent-[#1D9E75]"
              data-testid="modules-permission-checkbox"
            />
            <div className="flex-1">
              <div className="text-xs font-medium text-zinc-100">
                Permettre aux admins et contributeurs de modifier les modules
              </div>
              <div className="text-[10px] text-zinc-500 mt-0.5 leading-snug">
                Si activé : tout admin ou contributeur de votre organisation peut toggler les modules.
                Si désactivé : seul le super_admin (vous) peut le faire. Les lecteurs n&apos;ont jamais ce droit.
              </div>
            </div>
            {permBusy && <Loader2 className="w-3 h-3 animate-spin text-zinc-500" />}
          </label>
        </div>
      )}

      {!canToggle && role && role !== "lecteur" && (
        <div className="text-[11px] text-zinc-500 italic border border-white/5 bg-white/[0.02] rounded p-2">
          Lecture seule : votre super_admin n&apos;a pas autorisé les {role}s à modifier les modules.
        </div>
      )}

      <input
        type="text"
        placeholder="Filtrer un module…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="noam-input w-full text-xs"
        data-testid="modules-filter"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-[480px] overflow-y-auto pr-1" data-testid="modules-list">
        {modules.map((m) => {
          const disabled = m.ultra_locked || !canToggle || busy === m.id;
          const stateLabel = m.ultra_locked
            ? "Verrouillé par Ultra Admin"
            : m.effective_visible
              ? (m.in_plan ? "Inclus dans le plan" : "Activé hors plan")
              : (m.in_plan ? "Désactivé" : "Hors plan");
          const stateColor = m.ultra_locked
            ? "#E24B4A"
            : m.effective_visible
              ? (m.in_plan ? "#1D9E75" : "#EF9F27")
              : "#71717A";
          return (
            <label
              key={m.id}
              className={`flex items-center gap-2 p-2 rounded border ${m.effective_visible ? "bg-white/[0.03] border-white/10" : "bg-white/[0.01] border-white/5 opacity-70"} ${disabled ? "cursor-not-allowed" : "cursor-pointer hover:bg-white/[0.05]"}`}
              data-testid={`module-row-${m.id}`}
            >
              <input
                type="checkbox"
                checked={m.effective_visible}
                onChange={() => toggle(m)}
                disabled={disabled}
                className="accent-[#7F77DD]"
                data-testid={`module-toggle-${m.id}`}
              />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-zinc-100 truncate flex items-center gap-1">
                  {m.label}
                  {m.ultra_locked && <span title="Verrouillé par Ultra Admin">🔒</span>}
                </div>
                <div className="text-[10px] flex items-center gap-1.5 mt-0.5">
                  <span className="w-1 h-1 rounded-full" style={{ background: stateColor }} />
                  <span style={{ color: stateColor }}>{stateLabel}</span>
                </div>
              </div>
              {busy === m.id && <Loader2 className="w-3 h-3 animate-spin text-zinc-500" />}
            </label>
          );
        })}
        {modules.length === 0 && (
          <div className="col-span-2 text-xs text-zinc-500 italic py-4 text-center">
            Aucun module ne correspond au filtre.
          </div>
        )}
      </div>
    </div>
  );
}


function DangerZoneCard() {
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [loading, setLoading] = useState(false);

  const canDelete = password.length > 0 && confirmText.trim().toUpperCase() === "SUPPRIMER";

  const handleDelete = async () => {
    if (!canDelete) return;
    setLoading(true);
    try {
      await api.delete("/auth/account", { data: { password } });
      notify.success("Compte supprimé. À bientôt.");
      await logout();
      window.location.href = "/login";
    } catch (err) {
      notify.error(fmtError(err, "Suppression impossible."));
      setLoading(false);
    }
  };

  return (
    <div className="noam-card p-6 space-y-4 border-red-500/25 bg-red-500/[0.03]" data-testid="danger-zone-card">
      <div className="flex items-center gap-2 text-[10px] font-semibold tracking-[0.18em] uppercase text-red-400">
        <AlertTriangle className="w-3 h-3" /> Zone sensible
      </div>
      <div>
        <h2 className="text-base font-semibold text-zinc-100">Supprimer mon compte</h2>
        <p className="text-sm text-zinc-400 mt-1 leading-relaxed">
          Action définitive et irréversible (RGPD). Votre compte et vos données personnelles sont supprimés.
          Les organisations dont vous êtes le dernier membre sont également effacées.
        </p>
      </div>

      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold text-red-300 border border-red-500/40 hover:bg-red-500/10 transition-colors"
          data-testid="delete-account-open-btn"
        >
          <Trash2 className="w-4 h-4" /> Supprimer mon compte
        </button>
      ) : (
        <div className="space-y-3 border border-red-500/30 rounded-lg p-4 bg-black/20">
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-400 mb-1.5 block">Mot de passe actuel</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Confirmez avec votre mot de passe"
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-red-500 focus:outline-none"
              data-testid="delete-account-password-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-400 mb-1.5 block">
              Tapez <span className="text-red-300 font-semibold">SUPPRIMER</span> pour confirmer
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="SUPPRIMER"
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-red-500 focus:outline-none"
              data-testid="delete-account-confirm-input"
            />
          </div>
          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={handleDelete}
              disabled={!canDelete || loading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold bg-red-600 text-white hover:bg-red-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="delete-account-confirm-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              {loading ? "Suppression…" : "Supprimer définitivement"}
            </button>
            <button
              type="button"
              onClick={() => { setOpen(false); setPassword(""); setConfirmText(""); }}
              className="px-4 py-2 rounded-md text-sm text-zinc-400 hover:text-zinc-200 transition-colors"
              data-testid="delete-account-cancel-btn"
            >
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
